import React, {FC} from 'react';
import {
  Autocomplete,
  createTheme,
  FormControl,
  MenuItem,
  Paper,
  Select,
  TextField,
  ThemeProvider,
} from '@mui/material';
import {pageSizes, statuses} from 'core/constants';
import {SelectPopupIcon} from 'assets/icons';
import {searchAndSelect, selectRowsPaperProps} from 'styles/MUIStyles/common';
import search from 'assets/icons/search.svg';
import {IMetadata} from 'store/models/common';
import styles from '../CompanyOwners.module.scss';

interface Props {
  search: string;
  setSearch: (search: string) => void;
  status: {label: string; value: boolean; queryName: string};
  setStatus: (
    status: {label: string; value: boolean; queryName: string} | null,
  ) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  metaData: IMetadata | null;
}

const theme = createTheme({
  components: {
    MuiSelect: {
      styleOverrides: {
        root: {
          color: 'var(--text-secondary)',
          fontFamily: 'Poppins, sans-serif',
          fontSize: '14px',
          fontWeight: '400',
          lineHeight: '24px',
          '& .MuiOutlinedInput-notchedOutline': {
            border: '1px solid var(--border-strong)',
            borderRadius: '12px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            border: '1px solid var(--border-strong)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            border: '1px solid var(--primary)',
          },
        },
      },
    },
  },
});

const CompanyOwnersTopToolbar: FC<Props> = (props) => {
  return (
    <ThemeProvider theme={(outer) => createTheme(outer, {components: theme.components})}>
      <div className={styles.tableAutocompleteFilters}>
        <div className={styles.dealersTopToolbarSearch}>
          <input
            name="companyOwnersSearch"
            type="text"
            className="input"
            placeholder="Search"
            autoComplete="off"
            value={props.search}
            onChange={(e) => props.setSearch(e.target.value)}
          />
          <img className={styles.searchIcon} src={search} alt="search icon" />
        </div>
        <Autocomplete
          disablePortal
          options={statuses}
          value={props.status}
          onChange={(event, value) => {
            if (value) {
              props.setStatus(value);
            } else {
              props.setStatus(null);
            }
          }}
          PaperComponent={(props) => (
            <Paper
              sx={{
                borderRadius: '12px',
                boxShadow: 'var(--shadow-card)',
                marginTop: '8px',
                cursor: 'pointer',
              }}
              {...props}
            />
          )}
          sx={{width: '20%'}}
          ListboxProps={{style: {maxHeight: '200px', padding: 0}}}
          isOptionEqualToValue={(option, value) =>
            value.label === '' ? true : option.label === value.label
          }
          popupIcon={<SelectPopupIcon />}
          renderOption={(props: any, option) => {
            return (
              <span {...props} className={styles.dropDownItem}>
                {option.label}
              </span>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              placeholder="Select status"
              sx={searchAndSelect}
            />
          )}
        />
        <div className={styles.topToolbarSelectContainer}>
          {props?.metaData && (
            <>
              <span>Show rows:</span>
              <FormControl size="small">
                <Select
                  name="companiesOwnersPageCount"
                  value={+props.pageSize}
                  onChange={(e) => {
                    props.setPageSize(+e.target.value);
                  }}
                  IconComponent={(props) => <SelectPopupIcon {...props} />}
                  MenuProps={{
                    MenuListProps: {disablePadding: true},
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left',
                    },
                    PaperProps: selectRowsPaperProps,
                  }}
                >
                  {pageSizes.map((page) => {
                    return (
                      <MenuItem key={page} value={page}>
                        {page}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <span>{`${props?.metaData?.firstItemOnPage}-${props?.metaData?.lastItemOnPage} of ${props?.metaData?.totalItemCount}`}</span>
            </>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
};

export default CompanyOwnersTopToolbar;
